import { flatMap } from "lodash";
import * as React from "react";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";

import { isPathnameRestricted } from "@app/authorization";
import { ExportType } from "@app/exports/enumerations";
import { ProjectPerspective } from "@app/projects";
import { CountyProjectContextProps, withCountyProjectContext } from "@app/projects/CountyProjectContext";
import { ProjectRouteParams } from "@app/projects/projectRouteParams";
import { AccuracyAggregateInspectionResultGroup, AccuracyBandAggregate, NumericResultType } from "@app/projects/results/accuracy";
import { RegionGranularity } from "@app/region-sets";
import { TooltipWrapper } from "@app/shared";
import Pie from "@app/shared/pie";
import { ProgressBarBand } from "@app/shared/progressBarBand";
import { PrimaryTable as SkeletonTable } from "@app/shared/skeleton-components";
import { AccuracyTemplate, AccuracyTemplateContext } from "@app/templates";
import { AccuracyTemplateScoringBandGroup } from "@app/templates/accuracy";
import { UserContextProps, withUserContext } from "@app/user";
import { ColumnConfig, ColumnGroup, numberSort, SortDirections, TextAlignment, Waffles } from "@app/shared/waffles";
import { AccuracyTemplateInspection, AccuracyTemplateInspectionGroup, AccuracyTemplateScoringBand } from "@app/templates/accuracy/models";
import { AccuracyAggregate, AccuracyTooltipLegend, DetailsOnlyProps } from "../accuracy";
import { AccuracyProviderProfileFromQuery } from "@app/projects/servicing-provider/accuracy/AccuracyProviderProfileFromQuery";
import InformationalIndicator from "../InformationalSpecialtyGroupIndicator";
import { formatHelper } from "../formatHelper";
import { AttestationRateDays, FilterType } from "@app/projects/enumerators";
import { Features } from "@app/feature-flags";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { filterProviderClaimsActivityInspectionGroup } from "./AccuracyProviderClaimsActivityFilter";
import { filterPractitionerAtLocationConfidenceInspectionGroup } from "./AccuracyPractitionerAtLocationConfidenceFilter";
import { filterCertifiedSkilledMedicalInterpreterInspectionGroup } from "./AccuracyCertifiedSkilledMedicalInterpreterFilter";
import { filterGenderAffirmingCareInspectionGroup } from "./AccuracyGenderAffirmingCareFilter";
import { JSX } from "react";

type AccuracyAggregateDetailsProps = RouteComponentProps<unknown, unknown, ProjectRouteParams> & CountyProjectContextProps & UserContextProps & DetailsOnlyProps & FeatureFlagContextProps &{
  countyPerspectiveOnlyItems: boolean;
  exportEndpoint: string;
};

class AccuracyAggregateWaffleToppings {
  public accuracy: string | JSX.Element;
  public key: string;
  public providers: React.ReactNode;
  public recentlyAttestedPercent: React.ReactNode;
  public score: React.ReactNode;
  public title: JSX.Element;

  constructor(accuracy: string | JSX.Element, key: string, providers: React.ReactNode, score: React.ReactNode, title: JSX.Element, inspections: InspectionNode[], recentlyAttestedPercent: React.ReactNode) {
    this.title = title;
    this.key = key;
    this.accuracy = accuracy;
    this.providers = providers;
    this.score = score;

    for (const inspection of inspections) {
      Object.assign(this, { [inspection.inspectionUID]: inspection.element });
    }
    this.recentlyAttestedPercent = recentlyAttestedPercent;
  }
}

class InspectionNode {
  public element: React.ReactNode;
  public inspectionUID: string;

  constructor(inspectionUID: string, element: React.ReactNode) {
    this.element = element;
    this.inspectionUID = inspectionUID;
  }
}

interface AccuracyAggregateDetailsState {
  selectedRecords: AccuracyAggregate[];
  selectedSortingColor: string;
  sortedColumn: string;
}

class AccuracyAggregateDetails extends React.Component<AccuracyAggregateDetailsProps, AccuracyAggregateDetailsState> {
  private bands: AccuracyTemplateScoringBand[] = [];
  private inspectionGroups: AccuracyTemplateInspectionGroup[] = [];
  private sortingColorMap: Map<string, string[]> = new Map<string, string[]>();

  constructor(props: AccuracyAggregateDetailsProps) {
    super(props);

    this.state = {
      selectedRecords: [],
      selectedSortingColor: null,
      sortedColumn: null,
    };
  }

  public async componentDidMount() {
    if (this.props.location.state && this.props.location.state.runConfiguration) {
      const config = this.props.location.state.runConfiguration;
      const compareToConfig = this.props.location.state.compareToRunConfiguration;
      await this.props.setDualMembershipMode(this.props.location.state.isDualMembershipMode, config.projectRunConfigurationType, config.projectRunConfigurationId, compareToConfig ? compareToConfig.projectRunConfigurationId : undefined);
    }
  }

  public componentDidUpdate(prevProps: AccuracyAggregateDetailsProps) {
    if (prevProps.perspective !== this.props.perspective) {
      this.setState({
        selectedRecords: [],
      });
    }
  }

  public render() {
    const haveResultsToRender: boolean = !this.props.loadingResults && !!this.props.accuracyResult && !!this.props.accuracyResult.aggregateDetails;

    if (haveResultsToRender && this.sortingColorMap.size === 0) {
      this.buildSortingColorMaps();
    }

    return (
      <AccuracyTemplateContext.Consumer>
        {(accuracyTemplate) => (
          <>
            {(!haveResultsToRender || null) && (<span className="skeleton shimmer"><SkeletonTable /></span>)}
            {(haveResultsToRender || null) && (this.renderAccuracyAggregates(accuracyTemplate))}
          </>
        )}
      </AccuracyTemplateContext.Consumer>
    );
  }

  private buildSortingColorMaps = () => {
    // in order to avoid allowing the user to sort using colors that are not available,
    // only give the color options for which there is corresponding data for
    // inspection pie
    for (const aggregateDetail of this.props.accuracyResult.aggregateDetails) {
      for (const resultGroup of aggregateDetail.resultGroups) {
        for (const result of resultGroup.results) {
          const existingMapping = this.sortingColorMap.get(result.inspectionUid);

          // if a map for a particular result group has already been initialized,
          // add to said color map, rather than completely overwriting it
          if (existingMapping) {
            for (const band of result.bandAggregates) {
              if (!existingMapping.includes(band.color)) {
                existingMapping.push(band.color);
              }
            }

            this.sortingColorMap.set(result.inspectionUid, existingMapping);
          } else {
            const bandColors: string[] = [];

            for (const band of result.bandAggregates) {
              bandColors.push(band.color);
            }

            this.sortingColorMap.set(result.inspectionUid, bandColors);
          }
        }
      }

      // accuracy aggregate pie
      const existingAccuracyMapping = this.sortingColorMap.get("accuracy");

      // if a map for accuracy has already been initialized,
      // add to said color map, rather than completely overwriting it
      if (existingAccuracyMapping) {
        for (const accuracyBand of aggregateDetail.bands) {
          if (!existingAccuracyMapping.includes(accuracyBand.color)) {
            existingAccuracyMapping.push(accuracyBand.color);
          }
        }

        this.sortingColorMap.set("accuracy", existingAccuracyMapping);
      } else {
        const accuracyBandColors: string[] = [];

        for (const accuracyBand of aggregateDetail.bands) {
          accuracyBandColors.push(accuracyBand.color);
        }

        this.sortingColorMap.set("accuracy", accuracyBandColors);
      }
    }
  }

  private renderAccuracyAggregates(accuracyTemplate: AccuracyTemplate) {
    this.storeInspectionsAndBands(accuracyTemplate);

    return (
      <>
        <Waffles<AccuracyAggregateWaffleToppings>
          bulkActionBar={this.getBulkActionBar()}
          columns={this.getColumnConfig(accuracyTemplate)}
          columnGroups={this.getColumnGroups(accuracyTemplate, this.props.attestationRateDays)}
          customSort={this.customSort}
          data={this.buildWaffleToppings(this.props.accuracyResult.aggregateDetails, accuracyTemplate) || []}
          fullWidth={true}
          handleCheckboxChange={this.handleCheckboxChange}
          selectedKeys={this.state.selectedRecords.map((sr: AccuracyAggregate) => sr.key)}
          showCheckboxes={true}
          testId="accuracy-aggregate-details"
          useVerticalHeaders={true}
        />
        <AccuracyProviderProfileFromQuery {...this.props} />
      </>
    );
  }

  private storeInspectionsAndBands = (accuracyTemplate: AccuracyTemplate) => {
    const inspectionGroups: AccuracyTemplateInspectionGroup[] = [];
    const bands: AccuracyTemplateScoringBand[] = [];

    for (const inspectionGroup of accuracyTemplate.criteria.inspectionGroups) {
      if (!inspectionGroups.includes(inspectionGroup)) {
        inspectionGroups.push(inspectionGroup);
      }
    }

    for (const bandGroup of accuracyTemplate.scoring.bandGroups) {
      for (const band of bandGroup.bands) {
        if (!bands.includes(band)) {
          bands.push(band);
        }
      }
    }

    this.inspectionGroups = inspectionGroups;
    this.bands = bands;
  }

  private buildWaffleToppings = (records: AccuracyAggregate[], accuracyTemplate: AccuracyTemplate) => {
    if (records.length === 0) {
      return [];
    }

    // Only show informational specialty groups if:
    // a) We're looking at specialty groups.
    // b) One of the specialty groups is marked as such.
    // Otherwise, don't show the indicator, it's a waste of space.
    // NOTE: This is NOT a column as we don't want to deal with its interaction with ellipses.
    const showInformationalIndicator: boolean = this.props.perspective === ProjectPerspective.BySpecialty &&
      this.props.accuracyResult.aggregateDetails.some(({ specialtyGroupIsInformational }) => !!specialtyGroupIsInformational);

    const renderAggregatePie = (record: AccuracyAggregate) => {
      const bands: ProgressBarBand[] = this.getAccuracyProgressBarBands(accuracyTemplate.scoring.bandGroups, record.bands);
      return bands.length > 0 ? (
        <div className="large-pie-chart-container">
          <TooltipWrapper
            text={
              <AccuracyTooltipLegend
                inspectionName="Aggregated Accuracy"
                progressBarBands={bands}
              />
            }
            position="bottom"
          >
            <Pie key={`${record.key}-accuracy`} segments={bands.map((band) => ({ fill: band.color, percent: band.percent / 100 }))} />
          </TooltipWrapper>
        </div>
      ) : "N/A";
    };

    const renderPie = (resultGroups: AccuracyAggregateInspectionResultGroup[], inspectionGroupName: string, inspection: AccuracyTemplateInspection) => {
      const bands: ProgressBarBand[] = this.getProgressBarBands(resultGroups, inspectionGroupName, inspection);
      return bands.length > 0 ? (
        <div className="pie-chart-container">
          <TooltipWrapper
            text={
              <AccuracyTooltipLegend
                inspectionName={inspection.name}
                progressBarBands={bands}
              />
            }
            position="bottom"
          >
            <Pie segments={bands.map((band) => ({ fill: band.color, percent: band.percent / 100 }))} />
          </TooltipWrapper>
        </div>
      ) : "N/A";
    };

    const inspectionGroups: AccuracyTemplateInspectionGroup[] = accuracyTemplate ? accuracyTemplate.criteria.inspectionGroups : [];

    const waffleToppings: AccuracyAggregateWaffleToppings[] = records.map((sr) => {
      const accuracy = renderAggregatePie(sr);

      const key: string = sr.key;

      const title = (
        <div className="text-nowrap">
          <InformationalIndicator visible={showInformationalIndicator} isInformational={!!sr.specialtyGroupIsInformational} />
          {isPathnameRestricted(`${this.props.match.url}${sr.linkSuffix}`, this.props.user)
            ? <span>{`${sr.displayText}${sr.parentAbbreviation ? `, ${sr.parentAbbreviation}` : ""}`}</span>
            : <>
              <Link
                to={{
                  pathname: `${this.props.match.url}${sr.linkSuffix}`,
                  search: this.props.location.search,
                }}
              >
                {`${sr.displayText}${sr.parentAbbreviation ? `, ${sr.parentAbbreviation}` : ""}`}
              </Link>
            </>}
        </div>
      );

      const providers = formatHelper(sr.providerCount as number, NumericResultType.Count);

      const score = formatHelper(sr.accuracyIndex as number, NumericResultType.Score);

      const inspections = flatMap(inspectionGroups, (inspectionGroup) => inspectionGroup.inspections.map<InspectionNode>((inspection) => (
        {
          element: renderPie(sr.resultGroups, inspectionGroup.name, inspection),
          inspectionUID: inspection.uid,
        }
      )));

      const has90DayFlag = this.props.enabledFeatureFlags.has(Features.USE_90_DAY_ATTESTATIONS);

      const attestationPercent = this.props.attestationRateDays === AttestationRateDays.Ninety && has90DayFlag ?
      sr.ninetyDayAttestedPercent : sr.recentlyAttestedPercent;

      const formattedAttestationPercent = formatHelper(attestationPercent, NumericResultType.Percent);

      return new AccuracyAggregateWaffleToppings(accuracy, key, providers, score, title, inspections, formattedAttestationPercent);
    });

    return waffleToppings;
  }

  private getBulkActionBar = () => {
    const networkTypes = this.props.runConfiguration && this.props.runConfiguration.networkTypes || undefined;
    const exportNetworkProviderCount = this.state.selectedRecords.length > 0
      ? this.state.selectedRecords.reduce((acc, ad) => acc + ad.providerCount, 0)
      : this.props.accuracyResult.aggregateDetails?.reduce((acc, ad) => acc + ad.providerCount, 0);

    let specialtyGroupCode: string = null;
    let stateRegionCode: string = null;

    if (this.props.regionFilters) {
      specialtyGroupCode = this.props.regionFilters.specialty;
      stateRegionCode = this.props.regionFilters.state;
    }

    const selectedRecordsRegionCodes = this.state.selectedRecords.map(r => r.regionCode);
    const selectedRecordsRegionSetIds = this.state.selectedRecords.map(r => r.regionSetId);

    let selectedRegionSetIds = this.props.countyRegionSetIds.filter(c => selectedRecordsRegionSetIds.includes(c.regionSetId) || selectedRecordsRegionCodes.includes(c.state)).map(c => c.regionSetId);
    // if no records are selected selected, use the current state region set id as the "default" selection for export properties or if there is no state region set id use all county region set ids
    if (selectedRegionSetIds.length === 0) {
      if (stateRegionCode) {
        selectedRegionSetIds = this.props.countyRegionSetIds.filter(c => c.state === stateRegionCode).map(c => c.regionSetId);
      }
      else
        selectedRegionSetIds = this.props.countyRegionSetIds.map(id => id.regionSetId);
    }

    let selectedSpecialtyGroupCodes = this.state.selectedRecords.map((r) => r.specialtyGroupCode).filter((s) => !!s);
    if (selectedSpecialtyGroupCodes.length === 0) {
      if (specialtyGroupCode) {
        selectedSpecialtyGroupCodes = [specialtyGroupCode];
      }
    }

    const availableExportTypes = [ExportType.ActionableProviders, ExportType.StatusReport];
    if (this.props.enabledFeatureFlags.has(Features.ACCURACY_INSPECTION_SUMMARY_REPORT_ENHANCEMENT))
      availableExportTypes.push(ExportType.AllInspections);

    const regionGranularity = this.getRegionGranularity();
    const countyFilters = this.getCountyFilters(regionGranularity, selectedRecordsRegionCodes);
    const stateFilters = this.getStateFilters(regionGranularity, selectedRecordsRegionCodes);

    return (
      <span className="bulk-action-group">
        <Link
          to={{
            pathname: this.props.exportEndpoint,
            state: {
              accuracyFilters: this.props.accuracyFilters,
              activeModule: this.props.module,
              availableExportTypes,
              countyFilters: countyFilters,
              exportNetworkProviderCount,
              filters: this.props.filters,
              granularity: regionGranularity,
              networkTypes,
              perspective: this.props.perspective,
              projectResourceId: this.props.project.resource.resourceId,
              projectResourceName: this.props.project.resource.name,
              projectURL: this.props.location.pathname + this.props.location.search,
              regionSetIds: selectedRegionSetIds,
              specialtyGroupCodes: selectedSpecialtyGroupCodes,
              stateFilters: stateFilters
            },
          }}
        >
          <i className="fa fa-cloud-download mr-1" />
          Create Report
        </Link>
      </span>
    );
  }

  private getCountyFilters = (granularity : RegionGranularity.County | RegionGranularity.State, selectedRecordCodes : string[]) : string[] => {
    return (granularity == RegionGranularity.County
        && this.props.perspective == ProjectPerspective.ByCounty)
          ? selectedRecordCodes 
          : this.props.filters.filter(f => f.filterType == FilterType.County).map(f => f.filterValue)
  }

  private getStateFilters = (granularity: RegionGranularity.County | RegionGranularity.State, selectedRecordCodes: string[]) => {
    return (granularity == RegionGranularity.State
      && this.props.perspective == ProjectPerspective.ByState)
      ? selectedRecordCodes
      : this.props.filters.filter(f => f.filterType == FilterType.State).map(f => f.filterValue)
  }

  private getColumnConfig = (accuracyTemplate: AccuracyTemplate) => {
    let inspectionGroups: AccuracyTemplateInspectionGroup[] = filterProviderClaimsActivityInspectionGroup(accuracyTemplate ? accuracyTemplate.criteria.inspectionGroups : [],
      this.props.runConfiguration.excludeProviderClaimsActivityInspector || !this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_PROVIDER_CLAIMS_ACTIVITY));
    inspectionGroups = filterPractitionerAtLocationConfidenceInspectionGroup(accuracyTemplate && inspectionGroups ? inspectionGroups : [],
      this.props.runConfiguration.excludeAccuracyPractitionerAtLocationInspector || !this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_PRAC_AT_LOC_CONFIDENCE));
    inspectionGroups = filterCertifiedSkilledMedicalInterpreterInspectionGroup(accuracyTemplate && inspectionGroups ? inspectionGroups : [], !this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_LOCATION_CERTIFIED_SKILLED_MEDICAL_INTERPRETER));
    inspectionGroups = filterGenderAffirmingCareInspectionGroup(accuracyTemplate && inspectionGroups ? inspectionGroups : [], !this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_LOCATION_GENDER_AFFIRMING_CARE));

    const columns: ColumnConfig = {
      accuracy: {
        alignment: TextAlignment.Center,
        displayText: "",
        handleSpecialSort: this.setSelectedSortingColor,
        noResize: true,
        specialSort: this.createSpecialSortElement("accuracy"),
        testId: "accuracy",
      },
      providers: {
        alignment: TextAlignment.Center,
        displayText: "",
        noResize: true,
        testId: "providers",
      },
      recentlyAttestedPercent: {
        alignment: TextAlignment.Center,
        displayText: "",
        noResize: true,
        testId: "recentlyAttestedPercent",
      },
      score: {
        alignment: TextAlignment.Center,
        displayText: "",
        noResize: true,
        testId: "score",
      },
      title: {
        displayText: "",
        noResize: true,
        sticky: true,
        testId: "title",
      },
    };

    inspectionGroups.forEach((ig) => {
      ig.inspections.forEach((i) => {
        columns[i.uid] = {
          displayText: i.name,
          handleSpecialSort: this.setSelectedSortingColor,
          noResize: true,
          specialSort: this.createSpecialSortElement(i.uid),
          testId: i.uid
        };
      })
    })

    return columns;
  }

  private getColumnGroups = (accuracyTemplate: AccuracyTemplate, attestationRateDays: AttestationRateDays) => {
    const perspective: ProjectPerspective = this.props.perspective;
    const title = perspective === ProjectPerspective.ByState ?
      "State" :
      perspective === ProjectPerspective.ByCounty ?
        "County" :
        perspective === ProjectPerspective.BySpecialty ?
          "Specialty Group" :
          "";

    const filterProviderClaimsActivityInspection = this.props.runConfiguration.excludeProviderClaimsActivityInspector || !this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_PROVIDER_CLAIMS_ACTIVITY);
    const filterPracAtLocInspection = this.props.runConfiguration.excludeAccuracyPractitionerAtLocationInspector || !this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_PRAC_AT_LOC_CONFIDENCE);
    let inspectionGroups: AccuracyTemplateInspectionGroup[] = filterProviderClaimsActivityInspectionGroup(
      accuracyTemplate ? accuracyTemplate.criteria.inspectionGroups : [],
      filterProviderClaimsActivityInspection
    );
    inspectionGroups = filterPractitionerAtLocationConfidenceInspectionGroup(
      accuracyTemplate && inspectionGroups ? inspectionGroups : [],
      filterPracAtLocInspection
    );
    inspectionGroups = filterCertifiedSkilledMedicalInterpreterInspectionGroup(
      accuracyTemplate && inspectionGroups ? inspectionGroups : [],
      !this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_LOCATION_CERTIFIED_SKILLED_MEDICAL_INTERPRETER));
    inspectionGroups = filterGenderAffirmingCareInspectionGroup(
      accuracyTemplate && inspectionGroups ? inspectionGroups : [],
      !this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_LOCATION_GENDER_AFFIRMING_CARE));


    const has90DayFlag = this.props.enabledFeatureFlags.has(Features.USE_90_DAY_ATTESTATIONS);

    const columnGroups: Array<ColumnGroup<AccuracyAggregateWaffleToppings>> = [
      {
        children: ["title"],
        key: title,
        testId: title,
      },
      {
        alignment: TextAlignment.Center,
        children: ["accuracy"],
        key: "Accuracy",
        testId: "accuracy",
      },
      {
        alignment: TextAlignment.Center,
        children: ["providers"],
        key: "Providers",
        testId: "providers",
      },
      {
        alignment: TextAlignment.Center,
        children: ["score"],
        key: "Score",
        testId: "score",
      },
      ...inspectionGroups.map((ig) => {
        return (
          {
            children: ig.inspections.map((i) => i.uid as string as keyof AccuracyAggregateWaffleToppings),
            key: ig.name,
            testId: ig.uid,
          }
        );
      }),
      {
        alignment: TextAlignment.Center,
        children: ["recentlyAttestedPercent"],
        key: "Attestation %",
        testId: "recentlyAttestedPercent",
        tooltip: has90DayFlag ? `Percentage is based on a ${attestationRateDays}-day attestation rate.` : null,
      },
    ];

    return columnGroups;
  }

  private customSort = (records: AccuracyAggregateWaffleToppings[], column: keyof AccuracyAggregateWaffleToppings, direction: string) => {
    const data = [...records];
    const multiplier = direction === SortDirections.Asc ? 1 : -1;

    const inspections: string[] = [];
    for (const ig of this.inspectionGroups) {
      for (const i of ig.inspections) {
        inspections.push(i.uid);
      }
    }

    if (this.state.sortedColumn !== column) {
      this.setState({
        sortedColumn: column,
      });
    }

    if (inspections.includes(column)) {
      // sorting inspections
      let inspectionGroupName: string;
      let inspectionUid: string;
      for (const ig of this.inspectionGroups) {
        const inspection = ig.inspections.find((i) => i.uid === column);

        if (inspection) {
          inspectionUid = inspection.uid;
          inspectionGroupName = ig.name;
          break;
        }
      }

      const sortingColor = this.getSortingColor(column);

      if (sortingColor) {
        return data.sort((a, b) => (this.getInspectionBandPercentage(a, inspectionGroupName, inspectionUid, sortingColor) > this.getInspectionBandPercentage(b, inspectionGroupName, inspectionUid, sortingColor) ? 1 : -1) * multiplier);
      } else {
        return records;
      }
    } else {
      switch (column) {
        case "accuracy":
          const sortingColor: string = this.getSortingColor(column);

          if (sortingColor) {
            return data.sort((a, b) => (this.getAccuracyAggregateBandPercentage(a, sortingColor) > this.getAccuracyAggregateBandPercentage(b, sortingColor) ? 1 : -1) * multiplier);
          } else {
            return records;
          }
        case "providers":
        case "recentlyAttestedPercent":
        case "score":
          return numberSort(records, column, multiplier);
        case "title":
          return data.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
        default:
          console.warn(`No sort definition for column ${column}.`);
          return records;
      }
    }
  }

  private getSortingColor = (column: keyof AccuracyAggregateWaffleToppings) => {
    let sortingColor: string = null;
    if (this.state.selectedSortingColor && this.state.sortedColumn === column) {
      // if a selected sorting color exists and the last sorted column is this column,
      // use the selected sorting color stored in the state
      sortingColor = this.state.selectedSortingColor;
    } else {
      const colorMap = this.sortingColorMap.get(column);

      // if a sorting color hasn't been selected, or the last column that was sorted is not this column,
      // then select the first option from the color map
      if (colorMap) {
        sortingColor = colorMap[0];

        this.setState({
          selectedSortingColor: sortingColor,
        });
      }
    }

    return sortingColor;
  }

  private getAccuracyAggregateBandPercentage = (row: AccuracyAggregateWaffleToppings, sortingColor: string) => {
    const aggregateDetail = this.props.accuracyResult.aggregateDetails.find((a) => a.key === row.key);

    if (aggregateDetail) {
      const band = aggregateDetail.bands.find((b) => b.color === sortingColor);

      if (band) {
        return band.bandPercentage;
      }
    }

    return 0;
  }

  private getInspectionBandPercentage = (row: AccuracyAggregateWaffleToppings, inspectionGroupName: string, inspectionUid: string, sortingColor: string) => {
    const aggregateDetail = this.props.accuracyResult.aggregateDetails.find((a) => a.key === row.key);

    if (aggregateDetail) {
      const resultGroup = aggregateDetail.resultGroups.find((x) => x.groupName === inspectionGroupName);

      if (resultGroup) {
        const result = resultGroup.results.find((r) => r.inspectionUid === inspectionUid);

        if (result) {
          const bandAggregate = result.bandAggregates.find((b) => b.color === sortingColor);

          if (bandAggregate) {
            return bandAggregate.bandPercentage;
          }
        }
      }
    }

    return 0;
  }

  private setSelectedSortingColor = (column: string) => {
    // determines the next selected sorting color on circle click
    const colorMap = this.sortingColorMap.get(column);

    if (colorMap) {
      if (this.state.selectedSortingColor && this.state.sortedColumn === column) {
        const index = colorMap.indexOf(this.state.selectedSortingColor);

        if (index === colorMap.length - 1) {
          // if the currently selected sorting color is the last in the array of possible color options,
          // set the first option in said array as the next selected sorting color
          this.setState({
            selectedSortingColor: colorMap[0],
          });
        } else {
          // otherwise, select the next option
          this.setState({
            selectedSortingColor: colorMap[index + 1],
          });
        }
      } else {
        // if no color is selected or sorting is occurring in a new column,
        // set the first option in the array of possible color options as the next selected sorting color
        this.setState({
          selectedSortingColor: colorMap[0],
        });
      }
    }
  }

  private createSpecialSortElement = (column: string) => {
    const band = this.bands.find((x) => x.bandColor === this.state.selectedSortingColor);

    let tooltipText: JSX.Element;
    if (band) {
      const sortingColorMap: string[] = this.sortingColorMap.get(column);

      if (sortingColorMap && sortingColorMap.length > 1) {
        tooltipText = (
          <>
            Sorting By: {band.name}
          </>
        );
      } else {
        tooltipText = (
          <>
            All rows are {band.name}
          </>
        );
      }
    }

    return (
      <TooltipWrapper
        text={tooltipText}
        position="bottom"
      >
        <i
          className="fa fa-circle cursor-pointer icon-outline"
          style={{ color: this.state.selectedSortingColor }}
        />
      </TooltipWrapper>
    );
  }

  private getProgressBarBands = (resultGroups: AccuracyAggregateInspectionResultGroup[], groupName: string, inspection: AccuracyTemplateInspection) => {
    const resultGroup = resultGroups.find((r) => r.groupName === groupName);
    const result = resultGroup ? resultGroup.results.find((r) => r.inspectionUid === inspection.uid) : null;
    const bandAggregates = result && result.bandAggregates || [];

    return bandAggregates.map<ProgressBarBand>((a) => {
      return ({
        color: a.color,
        colorType: "hex",
        justification: a.justification,
        outOfScale: a.bandPercentage > 0 && a.bandPercentage < 0.1,
        percent: this.getBandPercent(a.bandPercentage),
        tooltip: a.name ? a.name : "",
      })
    });
  }

  private handleCheckboxChange = (selectedKeys: string[]) => {
    const selectedRecords = this.props.accuracyResult.aggregateDetails.filter((result) => selectedKeys.includes(`${result.key}`));

    this.setState({ selectedRecords });
  }

  private getAccuracyProgressBarBands(scoringBandGroups: AccuracyTemplateScoringBandGroup[], resultBands: AccuracyBandAggregate[]) {
    const progressBarBands: ProgressBarBand[] = [];

    if (resultBands) {
      const total = resultBands.reduce((sum, band) => sum + (band.inspectionCount * band.weightSummation), 0);

      for (const scoringBandGroup of scoringBandGroups) {
        for (const scoringBand of scoringBandGroup.bands) {
          const resultBand = resultBands.find((x) => x.bandUid === scoringBand.uid);

          if (resultBand) {
            const value = resultBand.inspectionCount * resultBand.weightSummation;
            const percent = resultBand ? (value / total * 100) : 0;

            const progressBarBand: ProgressBarBand = {
              color: scoringBand.bandColor,
              colorType: "hex",
              outOfScale: percent > 0 && percent < 0.1,
              percent: this.getBandPercent(percent),
              tooltip: scoringBand.name,
            };
            if (percent > 0) {
              progressBarBands.push(progressBarBand);
            }
          }
        }
      }
    }

    return progressBarBands;
  }

  private getRegionGranularity = () => {
    // if a county is selected or a state is selected, report at county granularity
    if (this.props.regionFilters.county || this.props.regionFilters.state || this.props.perspective === ProjectPerspective.ByCounty) {
      return RegionGranularity.County;
    }
    // if nothing is selected, the granularity is state
    return RegionGranularity.State;
  }

  // if the value is too small to render force smallest increment (0.1)
  private getBandPercent = (percent: number) => {
    return percent
      ? (percent > 0 && percent < 0.1) ? 0.3 : percent // 0.3 is the smallest visible segment
      : 0;
  }
}

const WrappedAccuracyAggregateDetails = withFeatureFlagContext(withUserContext(withCountyProjectContext(AccuracyAggregateDetails)));

export default WrappedAccuracyAggregateDetails;
